import { useEffect, useState } from "react";
import { Coins, CheckCircle, XCircle, Sparkle, FilmSlate, Trophy } from "@phosphor-icons/react";
import api from "../lib/api";

function Stat({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-[#18181B] p-6">
      <div className="flex items-center gap-2 overline mb-3"><Icon size={13} /> {label}</div>
      <div className="font-mono font-extrabold text-3xl" style={{ color }}>{value}</div>
      {sub && <div className="font-mono text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function MetricBar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between mb-2"><span className="font-mono text-sm">{label}</span><span className="font-mono font-bold text-primary">{value ?? "—"}<span className="text-muted-foreground text-xs">/5</span></span></div>
      <div className="h-1.5 bg-secondary"><div className="h-full bg-primary transition-all" style={{ width: `${((value || 0) / 5) * 100}%` }} /></div>
    </div>
  );
}

const METRIC_LABELS = { hook_strength: "Hook strength", pacing: "Pacing / flow", caption_quality: "Caption quality", visual_quality: "Visual quality", watchability: "Watchability" };

function ModeBlock({ title, data }) {
  if (!data || !data.count) return (
    <div className="bg-[#18181B] p-6"><div className="overline mb-4">{title}</div><div className="font-mono text-sm text-muted-foreground">no scores yet</div></div>
  );
  return (
    <div className="bg-[#18181B] p-6">
      <div className="flex items-center justify-between mb-5">
        <div className="overline">{title}</div>
        <div className="font-mono text-xs text-muted-foreground">{data.count} reviews</div>
      </div>
      <div className="space-y-4 mb-6">
        {data.avg && Object.entries(METRIC_LABELS).map(([k, label]) => <MetricBar key={k} label={label} value={data.avg[k]} />)}
      </div>
      <div className="grid grid-cols-3 gap-px bg-border border border-border">
        <div className="bg-[#18181B] p-3"><div className="overline" style={{ fontSize: "0.5rem" }}>orch wins</div><div className="font-mono font-bold text-[#00E559]">{data.orchestrated_wins}</div></div>
        <div className="bg-[#18181B] p-3"><div className="overline" style={{ fontSize: "0.5rem" }}>baseline</div><div className="font-mono font-bold">{data.baseline_wins}</div></div>
        <div className="bg-[#18181B] p-3"><div className="overline" style={{ fontSize: "0.5rem" }}>win rate</div><div className="font-mono font-bold text-primary">{data.win_rate}%</div></div>
      </div>
    </div>
  );
}

export default function VideoScorecard() {
  const [sc, setSc] = useState(null);
  useEffect(() => { api.get("/video/scorecard").then(({ data }) => setSc(data)).catch(() => {}); }, []);
  if (!sc) return <div className="font-mono text-muted-foreground">loading…</div>;

  const pass = sc.gate_pass;
  const hasScores = !!(sc.overall && sc.overall.count);

  return (
    <div>
      <div className="overline mb-2">video os · {sc.completed_runs} completed runs</div>
      <h1 className="font-mono font-extrabold tracking-tighter text-3xl mb-8">Video scorecard</h1>

      <div data-testid="video-decision-gate" className="border-2 p-6 mb-10 flex flex-wrap items-center gap-6" style={{ borderColor: pass ? "#00E559" : "#FF3333", background: pass ? "rgba(0,229,89,0.05)" : "rgba(255,51,51,0.05)" }}>
        {pass ? <CheckCircle size={44} weight="fill" color="#00E559" /> : <XCircle size={44} weight="fill" color="#FF3333" />}
        <div className="flex-1 min-w-[240px]">
          <div className="font-mono font-extrabold tracking-tighter text-2xl" style={{ color: pass ? "#00E559" : "#FF3333" }}>{pass ? "ORCHESTRATION THESIS: PASS" : "THESIS NOT YET MET"}</div>
          <div className="text-sm text-muted-foreground mt-1 font-mono">Gate: win-rate &gt; 50% AND QC pass-rate ≥ 80% AND core cost &lt; ${sc.cost_target}/finished-min (duration-weighted, excludes AI-motion runs).</div>
        </div>
        <div className="flex items-center gap-2 font-mono text-sm"><Trophy size={18} className="text-primary" /> win-rate {hasScores ? sc.overall.win_rate : 0}%</div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border mb-10">
        <Stat icon={FilmSlate} label="completed" value={sc.completed_runs} sub={`${sc.core_runs} core · ${sc.motion_runs} motion`} />
        <Stat icon={Coins} label="cost / finished min" value={sc.avg_cost_per_finished_min != null ? `$${sc.avg_cost_per_finished_min}` : "—"} sub={`core · dur-weighted · target < $${sc.cost_target}`} color={sc.avg_cost_per_finished_min == null ? "#A1A1AA" : sc.avg_cost_per_finished_min < sc.cost_target ? "#00E559" : "#FF3333"} />
        <Stat icon={CheckCircle} label="qc pass rate" value={sc.qc_pass_rate != null ? `${sc.qc_pass_rate}%` : "—"} sub="target ≥ 80%" color={sc.qc_pass_rate == null ? "#A1A1AA" : sc.qc_pass_rate >= 80 ? "#00E559" : "#FF3333"} />
        <Stat icon={Sparkle} label="premium motion" value={sc.motion_cost_per_finished_min != null ? `$${sc.motion_cost_per_finished_min}` : "—"} sub={sc.motion_runs ? `${sc.motion_runs} AI-motion run${sc.motion_runs > 1 ? "s" : ""} · not gated` : "no motion runs yet"} color="#A1A1AA" />
      </div>

      <div className="overline mb-4">blind a/b · orchestrated vs uploaded baseline</div>
      <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
        <ModeBlock title="Footage → Reel (edit)" data={sc.edit} />
        <ModeBlock title="Script → Reel (generate)" data={sc.generate} />
      </div>
    </div>
  );
}
